import { z } from "zod";

/**
 * The two LLM prompt modes B3 ships with -- barcode (B4, issue #376) is a
 * separate scan path with its own endpoint/schema (pantryBarcodeRequestSchema
 * below), not a third value here, so this stays a two-value enum.
 */
export const pantryIntakeModeSchema = z.enum(["shelf", "receipt"]);
export type PantryIntakeModeInput = z.infer<typeof pantryIntakeModeSchema>;

/**
 * multer puts multipart text fields on req.body as strings, so `mode`
 * arrives as `{ mode: "shelf" }` alongside the parsed file -- validate just
 * that field rather than the whole body.
 */
export const pantryIntakeRequestSchema = z.object({
  mode: pantryIntakeModeSchema,
});
export type PantryIntakeRequestInput = z.infer<typeof pantryIntakeRequestSchema>;

/**
 * Body schema for POST /pantry/intake/barcode (B4, issue #376).
 *
 * Deliberately LENIENT: digits only, 4-14 characters. That range covers
 * every barcode symbology the lookup chain might plausibly see (UPC-E 6-8,
 * EAN-8 8, UPC-A 12, EAN-13/GTIN-13 13, GTIN-14 14) without hard-rejecting
 * an unusual-length code the server has never seen before -- an unrecognized
 * shape should fall through the lookup chain to the manual-entry fallback,
 * not bounce as a 400. Strict UPC-A check-digit validation lives
 * client-side only (packages/shared/src/upc.ts, isValidUpcA) -- see that
 * module's doc comment for why it must not be duplicated here.
 *
 * Epic #738 S5 (issue #749): the floor was relaxed from 6 to 4 digits to
 * admit PLU codes (4-digit conventional, 5-digit organic/historical-GMO-
 * marker variants) typed manually -- the corpus notes PLU is typically
 * hand-keyed, not camera-scanned, so a floor that rejects them before they
 * ever reach `classifyBarcodeFamily`/`lookupPantryItemByUpc` would silently
 * block the entire PLU intake path this slice adds. This schema still only
 * validates digit shape + width; PLU vs GTIN vs variable-weight
 * classification happens server-side, after this schema accepts the body.
 */
const UPC_LENIENT_PATTERN = /^\d{4,14}$/;

export const pantryBarcodeRequestSchema = z
  .object({
    upc: z
      .string()
      .trim()
      .regex(UPC_LENIENT_PATTERN, "upc must be 4-14 digits"),
  })
  .strict();
export type PantryBarcodeRequestInput = z.infer<typeof pantryBarcodeRequestSchema>;
